import { ApolloQueryResult } from '@apollo/client';
import InfoIcon from '@atlaskit/icon/glyph/info';
import Tooltip from '@atlaskit/tooltip';
import {
  CreditCard,
  generatedTypesNew,
  InvoicePaymentMethod,
  InvoiceStatus,
  Maybe,
  PaymentMethod,
  PaymentMethodType,
  Refunds,
  SortDirection,
} from '@css-xp/graphtypes';
import { NEW_DATE_FORMAT, NEW_DATE_TIME_FORMAT } from '@atlassian/cssxp-typescommon';
import { DEFAULT_PAGE, DEFAULT_ROWS_PER_PAGE, NO_DETAILS_TO_SHOW } from '@atlassian/cssxp-utils-entitlement';
import { GloballyAllowedSidebar, transformFilterableFields } from '@atlassian/cssxp-utils-generic';
import { getIdentifier } from '@atlassian/cssxp-utils-invoice';
import {
  ColGroup,
  commonTabs,
  CustomDynamicResultTable,
  CustomFilterWrapper,
  CustomSearch,
  FormattedAddress,
  FormattedCurrencyV2,
  FormattedDate,
  getRouteUrl,
  getTextAndTitle,
  InvoiceUtil,
  OpenSidebarRow,
  routePaths,
  SearchResultColumn,
  TableBody,
  TableHead,
  useSidebar,
} from '@css-xp/base';
import { useApplicationContext } from '@css-xp/base/common-context';
import { RefetchOptions } from '@css-xp/base/common-tabs';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import memoize from 'lodash/memoize';
import React, { memo, useCallback, useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import InvoiceChildRefundRow from '../../invoices/src/components/invoice-child-refund-row';
import './invoice-tab-ig.scss';

interface InvoieIGProps {
  transactionAccountId?: string;
  nodes: any;
  totalRecords: number;
  loading: boolean;
  error?: Error;
  label?: string;
  refetch: (_variables?: RefetchOptions) => Promise<ApolloQueryResult<unknown>>;
  availablePaymentMethods?: Maybe<Maybe<PaymentMethod>[]> | undefined;
  isPaymentMethodSidebar?: boolean;
  isShipToPartySidebar?: boolean;
  showHeading?: boolean;
  shouldForceEnableFilters?: boolean;
}

interface FormData {
  searchTerm: string;
}

const FILTER_FIELDS = [
  {
    filterItem: 'Status',
    queryKey: 'status',
    iterableValues: transformFilterableFields<typeof InvoiceStatus>(InvoiceStatus),
  },
];

export const getIsFreeInvoice = memoize((paymentMethod?: InvoicePaymentMethod | null) => {
  return paymentMethod === InvoicePaymentMethod.NOPAYMENT;
});

const getSoldToAddress = (shipTo: generatedTypesNew.InvoiceBillAndShipTo | undefined | null) => {
  if (!shipTo?.address) {
    return 'No address information';
  } else {
    return (
      <span className="line-clamp-sold-to-address">
        <FormattedAddress
          addressLine1={shipTo.address?.address1}
          addressLine2={shipTo.address?.address2}
          city={shipTo.address?.city}
          country={shipTo.address?.country}
          postcode={shipTo.address?.postalCode}
          state={shipTo.address?.state}
          organizationName={shipTo?.fullName}
        />
      </span>
    );
  }
};

const getDueOnCell = (cellName: string) => {
  return (
    <th>
      <Tooltip content={'Overdues in open invoices will be marked in red'} tag="span">
        <InfoIcon label="Info" size="small" />
      </Tooltip>
      <span className="due-on-text">{cellName}</span>
    </th>
  );
};

const getColumns = memoize((isPaymentMethodSidebar: boolean, isShipToPartySidebar: boolean): SearchResultColumn[] => {
  const columns = [
    {
      name: 'Invoice number',
      customClassNames: isPaymentMethodSidebar || isShipToPartySidebar ? 'col-30' : undefined,
    },
    {
      name: 'Sold-to address',
    },
    {
      name: 'Due on',
      renderComponent: getDueOnCell('Due on'),
    },
    {
      name: 'Issued on',
    },
    {
      name: 'Payment method',
    },
    {
      name: 'Amount',
    },
  ];

  return columns.filter((column) => {
    if (isPaymentMethodSidebar) {
      return column.name !== 'Payment method';
    } else {
      return column.name !== 'Sold-to address';
    }
  });
});

const sortOptions = {
  sortFields: ['createdDate', 'id'],
  sortFieldsDirections: [SortDirection.DESC, SortDirection.DESC],
};

export const InvoiceTabIG = ({
  transactionAccountId,
  nodes,
  totalRecords,
  loading,
  error,
  refetch,
  label,
  availablePaymentMethods,
  isPaymentMethodSidebar = false,
  isShipToPartySidebar = false,
  showHeading = false,
  shouldForceEnableFilters = false,
}: InvoieIGProps) => {
  /* For PPC (Start) */
  const applicationContext = useApplicationContext();
  const isPartnerAccount = applicationContext?.partnerAccount;
  const [selectedTransactionAccount] = useLocalStorage(`${applicationContext?.atlassianAccountId}-selectedTxa`, '');
  const invoicesRoutePPC = getRouteUrl(routePaths.invoices, { txaId: selectedTransactionAccount });
  /* For PPC (End) */
  const [rowsPerPage, setRowsPerPage] = useState<number>(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_PAGE);
  const [filterOptions, setFilterOptions] = useState<{ key: string; value: string }[]>([]);
  const [searchOptions, setSearchOptions] = useState<{ key: string; value: string }[]>([]);
  const { isSidebarOpen } = useSidebar();
  const totalCount = totalRecords;
  const hasNoResults = totalCount === 0;

  const onSearch = useCallback(
    async (formData: FormData) => {
      const currentSearchOptions = [];
      const searchInput = formData?.searchTerm;
      if (searchInput) {
        currentSearchOptions.push({
          key: 'invoiceNumber',
          value: searchInput,
        });
      }
      setSearchOptions(currentSearchOptions);
      setCurrentPage(1);
      refetch({
        filterOptions: filterOptions.concat(currentSearchOptions),
        searchOptions: {
          first: rowsPerPage,
          offset: 0,
          sort: { ...sortOptions },
        },
      });
    },
    [refetch, filterOptions, rowsPerPage]
  );

  const onFilter = useCallback(
    (selectedFilters: { key: string; value: string }[]) => {
      setFilterOptions(selectedFilters);
      setCurrentPage(1);
      refetch({
        filterOptions: searchOptions.concat(selectedFilters),
        searchOptions: {
          first: rowsPerPage,
          offset: 0,
        },
      });
    },
    [refetch, searchOptions, rowsPerPage]
  );

  const renderFilters = () => {
    return (
      <div className="invoice-ig-table-filter">
        <CustomSearch placeholder={'Search all linked invoices'} onSearch={onSearch} isLoading={loading} />
        <CustomFilterWrapper filterFields={FILTER_FIELDS} handleFilterUpdate={onFilter} />
      </div>
    );
  };

  const handlePageChange = useCallback(
    (newPage: number) => {
      setCurrentPage(newPage);
      refetch({
        searchOptions: {
          first: rowsPerPage,
          offset: (newPage - 1) * rowsPerPage,
          sort: { ...sortOptions },
        },
        filterOptions: searchOptions.concat(filterOptions),
      });
    },
    [rowsPerPage, filterOptions, searchOptions, refetch]
  );

  const handleRowsChange = useCallback(
    (newRowPerPage: number) => {
      if (newRowPerPage === rowsPerPage) {
        return;
      }
      setCurrentPage(1);
      setRowsPerPage(newRowPerPage);
      refetch({
        searchOptions: {
          first: newRowPerPage,
          sort: { ...sortOptions },
        },
        filterOptions: searchOptions.concat(filterOptions),
      });
    },
    [rowsPerPage, filterOptions, searchOptions, refetch]
  );

  const getPaymentMethod = useCallback(
    (paymentId: string | undefined | null) => {
      if (paymentId) {
        const selectedPaymentMethod = availablePaymentMethods?.find((paymentMethod) => paymentMethod?.id === paymentId);
        if (selectedPaymentMethod) {
          if (selectedPaymentMethod?.type === PaymentMethodType.CARD) {
            const creditCard = selectedPaymentMethod as CreditCard;
            return <span>{`**** **** **** ${creditCard?.card?.last4Numbers ?? ''}`}</span>;
          } else if (selectedPaymentMethod?.type === PaymentMethodType.DEFERRED) {
            return <span>Payment on terms</span>;
          }
        }
      }
      return 'Not available';
    },
    [availablePaymentMethods]
  );

  const renderInvoiceRow = (invoice: any) => {
    const {
      id,
      invoiceNumber,
      dueDate,
      grandTotal,
      paymentCurrency,
      createdDate,
      paymentMethod,
      sourceSystem,
      status,
      dunningStatus,
      pdfLink,
      paymentMethodId,
      shipTo,
    } = invoice;

    const identifier = getIdentifier({
      id,
      invoiceNumber,
      sourceSystem,
    });

    return (
      <>
        <td title={getTextAndTitle(id)}>
          <commonTabs.InvoiceCell
            invoiceId={id}
            invoiceNumber={identifier}
            status={status}
            paymentError={dunningStatus?.length ? true : false}
            pdfLink={pdfLink}
            transactionAccountId={transactionAccountId}
            grandTotal={grandTotal}
            sourceSystem={sourceSystem || ''}
            paymentMethod={invoice.paymentMethod as InvoicePaymentMethod}
            {...(isPartnerAccount && { invoiceLink: `${invoicesRoutePPC}${invoiceNumber || id || ''}` })}
          />
        </td>
        {isPaymentMethodSidebar && <td>{getSoldToAddress(shipTo)}</td>}
        <td>
          {dueDate ? (
            <div
              className={classNames({
                'overdue-date': InvoiceUtil.isDueDateOverdue(status ?? '', !!invoice?.dunningStatus?.length, dueDate),
              })}
            >
              <FormattedDate date={dueDate} format={NEW_DATE_FORMAT} />
            </div>
          ) : (
            <span className="not-applicable">N/A</span>
          )}
        </td>

        <td>
          {createdDate ? (
            <FormattedDate date={createdDate} showTime={true} format={NEW_DATE_TIME_FORMAT} />
          ) : (
            <span className="not-applicable">N/A</span>
          )}
        </td>
        {!isPaymentMethodSidebar && <td>{getPaymentMethod(paymentMethodId)}</td>}
        <td className="invoice-amount-cell">
          {!getIsFreeInvoice(paymentMethod) && grandTotal ? (
            <FormattedCurrencyV2 value={grandTotal} currency={paymentCurrency} showCurrencyCode />
          ) : (
            'Free'
          )}
        </td>
      </>
    );
  };

  const columns = getColumns(isPaymentMethodSidebar, isShipToPartySidebar);
  return (
    <CustomDynamicResultTable
      heading={(isShipToPartySidebar || isPaymentMethodSidebar || showHeading) && label}
      isLoading={loading}
      error={error?.toString()}
      noResults={hasNoResults}
      testId="IG-invoice-tab"
      emptyStateProps={{
        header: '',
        description: NO_DETAILS_TO_SHOW,
      }}
      filters={renderFilters()}
      isPaginationEnabled={totalCount > DEFAULT_ROWS_PER_PAGE}
      totalCount={totalCount}
      rowsPerPage={rowsPerPage}
      currentPage={currentPage}
      onPageChange={handlePageChange}
      onRowChange={handleRowsChange}
      className="invoice-group-invoices-table"
      shouldForceEnableFilters={shouldForceEnableFilters}
    >
      <ColGroup columns={columns} />
      <TableHead columns={columns} />
      <TableBody>
        {(nodes ?? []).map((invoice: any, index: number) => {
          const { id, invoiceNumber, sourceSystem, refunds } = invoice;

          const identifier = getIdentifier({
            id,
            invoiceNumber,
            sourceSystem,
          });
          const rowPayload = {
            invoiceId: identifier,
          };

          return (
            <>
              {isShipToPartySidebar || isPaymentMethodSidebar ? (
                <tr
                  key={`invoice-table-row-${index}`}
                  className={classNames({
                    'grey-row': index % 2 !== 0,
                    'white-row': index % 2 === 0,
                  })}
                >
                  {renderInvoiceRow(invoice)}
                </tr>
              ) : (
                <OpenSidebarRow
                  key={`invoice-table-row-${index}`}
                  sidebar={GloballyAllowedSidebar.INVOICE_PREVIEW}
                  sidebarOptions={{
                    isOverlay: true,
                    sidebarWidth: '968px',
                  }}
                  payload={rowPayload}
                  isSelected={false}
                  data-testid={`invoice-table-row-${index}`}
                  className={classNames({
                    'grey-row': index % 2 !== 0,
                    'white-row': index % 2 === 0,
                  })}
                  hasDefaultClassName={false}
                >
                  {renderInvoiceRow(invoice)}
                </OpenSidebarRow>
              )}
              <>
                {!isEmpty(refunds) &&
                  (refunds ?? []).map((ccpCreditNote: Refunds, indexNum: number) => {
                    return (
                      <InvoiceChildRefundRow
                        key={`invoice-ig-refund-row-${indexNum}`}
                        parentColumns={columns.length}
                        hideActions={false}
                        refund={ccpCreditNote}
                        isSelected={isSidebarOpen}
                        index={index}
                      />
                    );
                  })}
              </>
            </>
          );
        })}
      </TableBody>
    </CustomDynamicResultTable>
  );
};

export default memo(InvoiceTabIG);
